import React from 'react';
import { IndexData } from '@/contexts/MarketDataContext';

interface Props {
  underlying: string;
  indexData?: IndexData | null;
  liveIV?: number | null;   // raw IV from Upstox feed (e.g. 0.1685)
  liveOI?: number | null;   // open interest from Upstox feed
}

export default function OptionChainStats({ underlying, indexData, liveIV, liveOI }: Props) {
  const statsMap: Record<string, { pcr: number; maxPain: number; atmStrike: number; ivRank: number; totalOI: string; ceOI: string; peOI: string }> = {
    NIFTY:      { pcr: 1.24, maxPain: 24200, atmStrike: 24400, ivRank: 42, totalOI: '4.82Cr', ceOI: '2.18Cr', peOI: '2.64Cr' },
    BANKNIFTY:  { pcr: 0.87, maxPain: 52500, atmStrike: 53000, ivRank: 58, totalOI: '2.14Cr', ceOI: '1.14Cr', peOI: '0.99Cr' },
    FINNIFTY:   { pcr: 1.05, maxPain: 23800, atmStrike: 24000, ivRank: 47, totalOI: '0.94Cr', ceOI: '0.46Cr', peOI: '0.48Cr' },
    SENSEX:     { pcr: 1.18, maxPain: 80000, atmStrike: 80500, ivRank: 39, totalOI: '1.62Cr', ceOI: '0.74Cr', peOI: '0.88Cr' },
    MIDCPNIFTY: { pcr: 1.12, maxPain: 12200, atmStrike: 12400, ivRank: 44, totalOI: '0.48Cr', ceOI: '0.22Cr', peOI: '0.26Cr' },
    MIDCAP150:  { pcr: 1.08, maxPain: 19800, atmStrike: 20000, ivRank: 41, totalOI: '0.36Cr', ceOI: '0.17Cr', peOI: '0.19Cr' },
  };

  const stats = statsMap[underlying] ?? statsMap['NIFTY'];

  const spotVal = indexData?.value;
  const spotChg = indexData?.change;
  const spotPct = indexData?.pct;

  // Use live IV if available, otherwise fall back to static ivRank display
  const ivDisplay = liveIV != null
    ? `${(liveIV * 100).toFixed(2)}%`
    : `${stats.ivRank}%`;

  const oiDisplay = liveOI != null
    ? liveOI >= 1e7
      ? `${(liveOI / 1e7).toFixed(2)}Cr`
      : liveOI >= 1e5
      ? `${(liveOI / 1e5).toFixed(2)}L`
      : liveOI.toLocaleString('en-IN')
    : stats.totalOI;

  return (
    <div className="card-elevated p-4 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground">{underlying} Options Stats</h3>
        {spotVal && (
          <div className="text-right">
            <p className="text-sm font-bold font-tabular text-foreground">{spotVal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            <p className={`text-2xs font-semibold font-tabular ${(spotChg ?? 0) >= 0 ? 'text-positive' : 'text-negative'}`}>
              {(spotChg ?? 0) >= 0 ? '+' : ''}{(spotChg ?? 0).toFixed(2)} ({(spotPct ?? 0) >= 0 ? '+' : ''}{(spotPct ?? 0).toFixed(2)}%)
            </p>
          </div>
        )}
      </div>

      {/* PCR Gauge */}
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1.5">
          <span className="text-muted-foreground">Put-Call Ratio (PCR)</span>
          <span className={`font-bold font-tabular ${stats.pcr > 1 ? 'text-positive' : 'text-negative'}`}>{stats.pcr}</span>
        </div>
        <div className="h-2.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${stats.pcr > 1 ? 'bg-positive' : 'bg-negative'}`}
            style={{ width: `${Math.min((stats.pcr / 2) * 100, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-2xs text-muted-foreground mt-0.5">
          <span>Bearish (PCR &lt; 0.7)</span>
          <span>Bullish (PCR &gt; 1.2)</span>
        </div>
      </div>

      <div className="space-y-3">
        {[
          { label: 'Max Pain Strike', val: `₹${stats.maxPain.toLocaleString('en-IN')}`, sub: 'Price where max options expire worthless' },
          { label: 'ATM Strike', val: `₹${stats.atmStrike.toLocaleString('en-IN')}`, sub: 'Closest to current spot' },
          { label: liveIV != null ? 'IV (Live)' : 'IV Rank', val: ivDisplay, sub: liveIV != null ? 'Implied volatility from live feed' : '52-week IV percentile' },
          { label: liveOI != null ? 'OI (Live)' : 'Total OI', val: oiDisplay, sub: liveOI != null ? 'Open interest from live feed' : 'All strikes combined' },
          { label: 'CE Total OI', val: stats.ceOI, sub: 'Call side open interest' },
          { label: 'PE Total OI', val: stats.peOI, sub: 'Put side open interest' },
        ].map((s) => (
          <div key={`stat-${s.label}`} className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-foreground">{s.label}</p>
              <p className="text-2xs text-muted-foreground">{s.sub}</p>
            </div>
            <span className={`text-sm font-bold font-tabular ml-2 ${s.label.includes('Live') ? 'text-primary' : 'text-foreground'}`}>{s.val}</span>
          </div>
        ))}
      </div>

      {/* Sentiment */}
      <div className="mt-4 pt-3 border-t border-border">
        <p className="text-2xs text-muted-foreground mb-1.5">Market Sentiment</p>
        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
          stats.pcr > 1 ? 'bg-positive-subtle text-positive border border-positive/20' : 'bg-negative-subtle text-negative border border-negative/20'
        }`}>
          {stats.pcr > 1 ? '🐂 Mildly Bullish' : '🐻 Mildly Bearish'}
        </div>
      </div>
    </div>
  );
}